/**
 * One Suggested-work list render, as analytics sees it. Both the composer
 * shelf and the tray mint a `render_id`, stamp each row with its slot, and
 * report impressions; a pick reports the same `render_id` + `slot_index` so
 * the warehouse can join click to impression and, on leftover rows, `task_id`
 * to `clinical_task`.
 *
 * Nothing here reads a title, a name, or prompt text: every feature is a
 * closed enum, a bounded integer, or a pseudonymous id.
 */
import {
  workHomeSuggestionAnalyticsKind,
  type TrackWorkHomeSuggestionPicked,
  type TrackWorkHomeSuggestionShown,
  type WorkHomeAgeBucket,
  type WorkHomeFocusKind,
  type WorkHomeSuggestionAnalyticsFeatures,
} from '../../../contracts';
import { workHomeAgeBucket, type WorkHomeWorkItem } from '../../../data';

type WorkHomeRenderItem = Pick<WorkHomeWorkItem, 'id' | 'task' | 'analytics'>;

type WorkHomeShelfFeatures = Pick<
  WorkHomeSuggestionAnalyticsFeatures,
  'leftover_count' | 'open_task_count' | 'visit_age_bucket'
> & { focus_kind: WorkHomeFocusKind };

export interface WorkHomeRenderRecord {
  renderId: string;
  /** Slot-ordered, so `features[i]` belongs to the i-th item rendered. */
  features: readonly WorkHomeSuggestionAnalyticsFeatures[];
  featuresById: ReadonlyMap<string, WorkHomeSuggestionAnalyticsFeatures>;
}

export const EMPTY_WORK_HOME_RENDER: WorkHomeRenderRecord = {
  renderId: '',
  features: [],
  featuresById: new Map(),
};

const mintRenderId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `whr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const rowKind = (item: WorkHomeRenderItem): string =>
  workHomeSuggestionAnalyticsKind(item.id, { leftoverTask: Boolean(item.task) });

/** Bucketed, never a raw day count: days + event time would rebuild the date of service. */
export const workHomeVisitAgeBucket = (
  visitDate: Date | null | undefined,
  now: Date
): WorkHomeAgeBucket | undefined =>
  visitDate ? workHomeAgeBucket(visitDate, now) : undefined;

/** Report one list render: one impression per row, all sharing a render_id. */
export const recordWorkHomeRender = ({
  items,
  shelf,
  onShown,
}: {
  items: readonly WorkHomeRenderItem[];
  shelf: WorkHomeShelfFeatures;
  onShown?: TrackWorkHomeSuggestionShown;
}): WorkHomeRenderRecord => {
  const renderId = mintRenderId();
  const features = items.map<WorkHomeSuggestionAnalyticsFeatures>((item, slot_index) => ({
    ...item.analytics,
    ...shelf,
    render_id: renderId,
    slot_index,
    slot_count: items.length,
  }));
  items.forEach((item, index) => onShown?.(rowKind(item), features[index]));
  return {
    renderId,
    features,
    featuresById: new Map(items.map((item, index) => [item.id, features[index]!])),
  };
};

/** Report a pick with the features its impression carried. Returns the row's analytics kind. */
export const recordWorkHomePick = (
  record: WorkHomeRenderRecord,
  item: WorkHomeRenderItem,
  onPicked?: TrackWorkHomeSuggestionPicked
): string => {
  const kind = rowKind(item);
  onPicked?.(kind, record.featuresById.get(item.id));
  return kind;
};
